import type { Direction} from '../sim/direction';
import { directionForDelta } from '../sim/direction';

/**
 * Pure mapping from the set of currently-held key codes to a facing. Kept free
 * of any DOM types so it unit-tests in node and stays reusable; the render layer
 * owns the actual keydown/keyup listeners that maintain the held-key set.
 *
 * Arrow keys move in screen space (Up = screen-north, Right = screen-east, ...);
 * two perpendicular keys combine into a diagonal, and opposing keys cancel.
 */

const UP = new Set(['ArrowUp', 'KeyW']);
const DOWN = new Set(['ArrowDown', 'KeyS']);
const LEFT = new Set(['ArrowLeft', 'KeyA']);
const RIGHT = new Set(['ArrowRight', 'KeyD']);
const RUN = new Set(['ShiftLeft', 'ShiftRight']);

function any(codes: Set<string>, group: Set<string>): boolean {
  for (const c of codes) if (group.has(c)) return true;
  return false;
}

export function directionFromKeys(codes: Set<string>): Direction | null {
  // Screen-space intent: +x is screen-right, +y is screen-down.
  const sx = (any(codes, RIGHT) ? 1 : 0) - (any(codes, LEFT) ? 1 : 0);
  const sy = (any(codes, DOWN) ? 1 : 0) - (any(codes, UP) ? 1 : 0);
  if (sx === 0 && sy === 0) return null;

  // Convert screen intent to a tile-space delta and reuse the canonical
  // direction table. tileToScreen maps tile (1,0)->screen(+,+) etc., so the
  // inverse of the screen vector (sx,sy) is the tile delta below.
  const dx = (sx + sy) / 2;
  const dy = (sy - sx) / 2;
  return directionForDelta(dx, dy);
}

/**
 * Pure predicate for the run gait: true while either Shift code is held,
 * independent of any direction keys. `main.ts` pairs it with
 * {@link directionFromKeys} to drive `Mobile.setIntent(dir, running)`.
 */
export function runningFromKeys(codes: Set<string>): boolean {
  return any(codes, RUN);
}
